import { useEffect } from 'react'
import { createPortal } from 'react-dom'
import Icon from './Icon.jsx'
import Button from './Button.jsx'

/*
 * Internal modals — DESIGN.md §8. ABSOLUTE RULE: never use the browser's
 * alert/confirm/prompt. Every confirmation/notification goes through these.
 * Provided via ModalProvider (useModal) so any screen can call confirm()/notify().
 */
export function Modal({ open, onClose, children, maxWidth = 460, closeOnBackdrop = true }) {
  useEffect(() => {
    if (!open) return undefined
    const onKey = (e) => e.key === 'Escape' && onClose?.()
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, onClose])

  if (!open) return null
  // Portal to <body> so position:fixed is always relative to the viewport, even
  // when an ancestor (e.g. the topbar) creates a containing block via transform/filter.
  return createPortal(
    <div
      role="presentation"
      onMouseDown={(e) => closeOnBackdrop && e.target === e.currentTarget && onClose?.()}
      style={{
        position: 'fixed', inset: 0, zIndex: 1000,
        background: 'rgba(33,19,57,.42)', backdropFilter: 'blur(2px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20,
        animation: 'vbackdrop .15s ease',
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        style={{
          width: '100%', maxWidth, background: '#fff',
          borderRadius: 'var(--radius-card-lg)', boxShadow: 'var(--shadow-pop)',
          animation: 'vpop .18s ease', overflow: 'hidden',
        }}
      >
        {children}
      </div>
    </div>,
    document.body,
  )
}

const ICON_TONE = {
  success: { color: 'var(--color-success)', bg: 'var(--color-success-bg)', icon: 'check' },
  info: { color: 'var(--color-primary)', bg: 'var(--color-bg-lilac-strong)', icon: 'info' },
  error: { color: 'var(--color-danger)', bg: 'var(--color-danger-bg)', icon: 'alert-triangle' },
  danger: { color: 'var(--color-danger)', bg: 'var(--color-danger-bg)', icon: 'alert-triangle' },
}

function RoundIcon({ tone }) {
  const t = ICON_TONE[tone] || ICON_TONE.info
  return (
    <div style={{
      width: 48, height: 48, borderRadius: '50%', flex: 'none',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      color: t.color, background: t.bg,
    }}>
      <Icon name={t.icon} size={24} />
    </div>
  )
}

/* Notification modal — important info needing a read. Single "Compris" button. */
export function NotifyModal({ open, tone = 'info', title, message, onClose, closeLabel = 'Compris' }) {
  return (
    <Modal open={open} onClose={onClose}>
      <div style={{ padding: '28px 28px 24px' }}>
        <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start' }}>
          <RoundIcon tone={tone} />
          <div style={{ flex: 1, paddingTop: 2 }}>
            <h3 style={{ fontFamily: 'var(--font-title)', fontSize: 18, fontWeight: 700, color: 'var(--color-ink)', marginBottom: 6 }}>{title}</h3>
            {message && <p style={{ color: 'var(--color-text)', fontSize: 14 }}>{message}</p>}
          </div>
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 22 }}>
          <Button variant="primary" icon="check" onClick={onClose}>{closeLabel}</Button>
        </div>
      </div>
    </Modal>
  )
}

/* Confirmation modal — only for destructive/irreversible actions (DESIGN.md §8).
 * title = the question, message = the concrete consequence, confirm label = the verb. */
export function ConfirmModal({
  open, tone = 'danger', title, message,
  confirmLabel = 'Confirmer', cancelLabel = 'Annuler',
  destructive = true, onConfirm, onCancel,
}) {
  return (
    <Modal open={open} onClose={onCancel}>
      <div style={{ padding: '28px 28px 24px' }}>
        <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start' }}>
          <RoundIcon tone={tone} />
          <div style={{ flex: 1, paddingTop: 2 }}>
            <h3 style={{ fontFamily: 'var(--font-title)', fontSize: 18, fontWeight: 700, color: 'var(--color-ink)', marginBottom: 6 }}>{title}</h3>
            {message && <p style={{ color: 'var(--color-text)', fontSize: 14 }}>{message}</p>}
          </div>
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 22 }}>
          <Button variant="secondary" onClick={onCancel}>{cancelLabel}</Button>
          <Button
            variant={destructive ? 'danger' : 'primary'}
            icon={destructive ? 'trash' : 'check'}
            onClick={onConfirm}
          >
            {confirmLabel}
          </Button>
        </div>
      </div>
    </Modal>
  )
}
